import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { Card } from '../ui/card';
import matchesFocusVisible from './matchesFocusVisible';

const TITLE_FONTS = {
  noir: 'Playfair Display, serif',
  '90s_nyc': 'Montserrat, sans-serif',
};

const LOCK_MESSAGES = {
  locked: 'Complete Tutorial to Unlock',
  unavailable: 'Temporarily unavailable',
};

const ACTION_LABEL_SUFFIX = {
  unlocked: '',
  loading: ', loading',
  locked: ', locked until the tutorial is complete',
  unavailable: ', temporarily unavailable',
};

// Today's generated-theme card, unchanged in look. The only structural change
// is the click target: a stretched native <button> (the FeaturedCaseCard
// pattern) instead of an onClick on the Card div, so the card is reachable by
// Tab and operable by Enter/Space.
//
// The Card deliberately carries NO data-testid: ThemeSelection.test.js tells
// theme cards from featured cards by that attribute being absent.
const FreshCaseCard = ({ theme, index = 0, status, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const selectable = status === 'unlocked';
  // "Complete Tutorial to Unlock" is a claim about this player's progress, so
  // it is only shown once we actually heard back (never while loading).
  const showLock = status === 'locked' || status === 'unavailable';
  const isActive = selectable && (isHovered || isFocused);

  return (
    <Card
      data-theme-id={theme.id}
      className={`relative overflow-hidden transition-all duration-300 border-2 ${
        showLock ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
      } ${
        isActive ? 'scale-105 shadow-2xl' : 'scale-100 shadow-lg'
      } bg-slate-900 border-slate-700 ${selectable ? theme.hoverBorder : ''}`}
      style={{
        height: '450px',
        animationDelay: `${index * 100}ms`,
        boxShadow: isActive ? `0 20px 60px rgba(${theme.accentRgb}, 0.4)` : undefined,
      }}
      onMouseEnter={() => selectable && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section - 40% */}
      <div className="h-[180px] overflow-hidden">
        <img
          src={theme.image}
          alt={theme.name}
          className={`w-full h-full object-cover transition-transform duration-300 ${
            isActive ? 'scale-110' : 'scale-100'
          }`}
        />
      </div>

      {/* Content Section - 60% */}
      <div className={`p-6 h-[270px] flex flex-col justify-between bg-gradient-to-br ${theme.gradient}`}>
        <div>
          <h2
            className="text-3xl font-bold text-white mb-2"
            style={{ fontFamily: TITLE_FONTS[theme.id] || 'sans-serif' }}
          >
            {theme.name}
          </h2>
          <p className="text-sm font-semibold text-gray-300 mb-4 tracking-wide">{theme.era}</p>
          <p className="text-gray-200 leading-relaxed">{theme.description}</p>
        </div>

        {/* `cursor-[inherit]` keeps today's cursor: the ::after hit area covers
            the whole card, and a disabled button would otherwise show the
            preflight `default` cursor instead of the Card's pointer/not-allowed. */}
        <button
          type="button"
          data-testid="fresh-case-action"
          aria-label={`Select ${theme.name} case${ACTION_LABEL_SUFFIX[status] || ''}`}
          disabled={!selectable}
          className={`flex w-full items-center justify-between border-0 bg-transparent p-0 text-left cursor-[inherit] after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset ${theme.focusRing}`}
          onClick={() => onSelect(theme.id)}
          onFocus={(event) => setIsFocused(matchesFocusVisible(event.currentTarget))}
          onBlur={() => setIsFocused(false)}
        >
          <span className={`${theme.accentText} font-semibold uppercase tracking-wider text-sm`}>
            Select Case
          </span>
          <svg
            aria-hidden="true"
            className={`w-6 h-6 ${theme.accentText} transition-transform duration-300 ${
              isActive ? 'translate-x-2' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>

      {showLock && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-10">
          <div className="text-center">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-300 font-semibold">{LOCK_MESSAGES[status]}</p>
          </div>
        </div>
      )}
    </Card>
  );
};

export default FreshCaseCard;
